import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiGateway } from "./index";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Routes } from "@utils";

const ENROLMENT_ROOT = "/app/registro/firma-electronica-avanzada";
const BANK_TRANSFER_ROUTE = `${ENROLMENT_ROOT}/transferencia-bancaria`;
const CREATE_PIN_ROUTE = `${ENROLMENT_ROOT}/crear-pin`;
const VALIDATION_CHALLENGE_ROUTE = `${ENROLMENT_ROOT}/validation-challenge`;

export enum ENROLMENT_STEP {
  CI_DOC_NUM = "CI_DOC_NUM",
  CLAVE_UNICA = "CLAVE_UNICA",
  QUESTIONS_CHALLENGE = "QUESTIONS_CHALLENGE",
  BANK_TRANSACTION_CHALLENGE = "BANK_TRANSACTION_CHALLENGE",
  CERTIFICATE = "CERTIFICATE",
}

export enum ENROLMENT_STEP_STATUS {
  NOT_STARTED = "NOT_STARTED",
  STARTED = "STARTED",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}
export interface ParsedEnrolmentStatus {
  step: ENROLMENT_STEP | null;
  status: ENROLMENT_STEP_STATUS | null;
}

export interface EnrolmentStatus {
  validationStep: ENROLMENT_STEP;
  validationStepStatus: ENROLMENT_STEP_STATUS;
}

interface Value {
  currentStep: ENROLMENT_STEP | null;
  currentStepStatus: ENROLMENT_STEP_STATUS | null;
  setCurrentStep: (step: ENROLMENT_STEP) => void;
  setCurrentStepStatus: (status: ENROLMENT_STEP_STATUS) => void;
  error: Error | null;
  isLoading: boolean;
  getEnrolmentStatus: () => Promise<ParsedEnrolmentStatus | null>;
}

const EnrolmentStatusContext = createContext<Value | null>(null);

export const useEnrolmentStatusContext = () => {
  const value = useContext(EnrolmentStatusContext);
  if (!value) {
    throw new Error("EnrolmentStatusContext: No value provided");
  }
  return value;
};

function parseEnrolmentResponse(
  data: EnrolmentStatus | undefined,
): ParsedEnrolmentStatus {
  if (data == null) {
    return { step: null, status: null };
  }
  const step = data.validationStep != null ? data.validationStep : null;
  const status =
    data.validationStepStatus != null ? data.validationStepStatus : null;
  return { step, status };
}

export const EnrolmentStatusContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [currentStep, setCurrentStep] = useState<ENROLMENT_STEP | null>(null);
  const [currentStepStatus, setCurrentStepStatus] =
    useState<ENROLMENT_STEP_STATUS | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { requestId } = location.state || {};

  const getEnrolmentStatus = useCallback(
    async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Invalidate cache first to force a fresh fetch
        await queryClient.invalidateQueries({
          queryKey: ["enrolmentStatus"],
        });
        const data = await queryClient.fetchQuery<EnrolmentStatus>({
          queryKey: ["enrolmentStatus"],
          queryFn: () =>
            apiGateway<EnrolmentStatus>({
              url: "/enrolment/orchestrator",
            }),
          staleTime: 0, // Always consider data as stale to force refetch
        });
        const parsed = parseEnrolmentResponse(data);
        setCurrentStep(parsed.step);
        setCurrentStepStatus(parsed.status);
        return parsed;
      } catch (err) {
        setError(err as Error);
        const isAlreadyEnrolled =
          axios.isAxiosError(err) && err.response?.status === 409;
        if (isAlreadyEnrolled) {
          enqueueSnackbar("Usuario ya habilitado para firmar", {
            variant: "info",
          });
        } else {
          enqueueSnackbar("Error al registrar firma", { variant: "error" });
        }
        navigate(Routes.SOLICITUDES);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient, enqueueSnackbar, navigate],
  );

  const hasRequestedInitialStatus = useRef(false);

  useEffect(() => {
    if (currentStep || hasRequestedInitialStatus.current) return;
    hasRequestedInitialStatus.current = true;
    getEnrolmentStatus();
  }, [currentStep, getEnrolmentStatus]);

  useEffect(() => {
    const redirectTocorrectUrl = () => {
      if (!currentStep) return;
      const isUnder = (route: string) => location.pathname.includes(route);

      if (
        currentStep === ENROLMENT_STEP.BANK_TRANSACTION_CHALLENGE &&
        (currentStepStatus === ENROLMENT_STEP_STATUS.NOT_STARTED ||
          currentStepStatus === ENROLMENT_STEP_STATUS.STARTED) &&
        !isUnder(BANK_TRANSFER_ROUTE)
      ) {
        navigate(BANK_TRANSFER_ROUTE);
      } else if (
        currentStep === ENROLMENT_STEP.CERTIFICATE &&
        currentStepStatus === ENROLMENT_STEP_STATUS.NOT_STARTED &&
        !isUnder(CREATE_PIN_ROUTE)
      ) {
        const queryParams = requestId ? `?requestId=${requestId}` : "";
        navigate(`${CREATE_PIN_ROUTE}${queryParams}`);
      } else if (
        currentStep !== ENROLMENT_STEP.CERTIFICATE &&
        currentStep !== ENROLMENT_STEP.BANK_TRANSACTION_CHALLENGE &&
        !isUnder(VALIDATION_CHALLENGE_ROUTE)
      ) {
        navigate(VALIDATION_CHALLENGE_ROUTE);
      }
    };
    redirectTocorrectUrl();
  }, [currentStep, currentStepStatus, navigate, location.pathname, requestId]);

  const value = useMemo(
    () => ({
      currentStep,
      currentStepStatus,
      setCurrentStep,
      setCurrentStepStatus,
      error,
      isLoading,
      getEnrolmentStatus,
    }),
    [currentStep, currentStepStatus, error, isLoading, getEnrolmentStatus],
  );

  return (
    <EnrolmentStatusContext.Provider value={value}>
      {children}
    </EnrolmentStatusContext.Provider>
  );
};
